import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Pagination,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { useRole } from '../../hooks/useRole';
import { useAuth } from '../../contexts/AuthContext';
import RoleGuard, { AdminOnly, ManagerOrAbove } from '../RoleGuard';
import {
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  UserInfo,
  UserQueryModel,
  UserListResponse,
  ApiResponse
} from '../../types/user';
import { ROLES } from '../../utils/roleUtils';

const UserManagement: React.FC = () => {
  const { isAdmin, isAdminOrManager, canCreateUser, canEditData, canViewData, roleId } = useRole();
  const { user: currentUser } = useAuth();

  // 狀態管理
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });

  // 分頁和搜索狀態
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [activeFilter, setActiveFilter] = useState('ALL');

  // 表單狀態
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    userId: '',
    password: '',
    confirmPassword: '',
    nickName: '',
    email: '',
    role: ROLES.STAFF,
    isActive: true
  });

  const [updateForm, setUpdateForm] = useState<UpdateUserRequest>({
    userId: '',
    nickName: '',
    role: ROLES.STAFF,
    isActive: true
  });

  const [passwordForm, setPasswordForm] = useState<ChangePasswordRequest>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 載入用戶列表
  const loadUsers = async (page: number = pagination.page, pageSize: number = pagination.pageSize) => {
    try {
      setLoading(true);
      const response: UserListResponse = await apiService.getUsers({
        page,
        pageSize,
        searchTerm: searchTerm || undefined,
        roleFilter: roleFilter === 'ALL' ? undefined : roleFilter || undefined,
        activeFilter: activeFilter === 'ALL' ? undefined : activeFilter === 'true' ? true : false
      });

      // 如果當前用戶是 manager，過濾掉 admin 用戶（額外保護）
      // 後端已經過濾了，這裡只是作為額外的安全措施
      let filteredUsers = response.users;
      if (roleId === ROLES.MANAGER) {
        filteredUsers = response.users.filter(user => user.roleId !== ROLES.ADMIN);
      }

      setUsers(filteredUsers);
      setPagination({
        page: response.page,
        pageSize: response.pageSize,
        totalCount: response.totalCount, // 後端已經正確計算了總數
        totalPages: Math.ceil(response.totalCount / response.pageSize)
      });
    } catch (error: any) {
      showSnackbar('載入用戶列表失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewData) {
      loadUsers();
    }
  }, [canViewData]);

  // 搜索和過濾功能
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadUsers(1, pagination.pageSize);
  };

  const handleFilterChange = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    //loadUsers(1, pagination.pageSize);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, page }));
    loadUsers(page, pagination.pageSize);
  };

  const handlePageSizeChange = (event: any) => {
    const newPageSize = parseInt(event.target.value);
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
    loadUsers(1, newPageSize);
  };

  const handleRefresh = () => {
    loadUsers(pagination.page, pagination.pageSize);
  };

  // 顯示提示訊息
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  // 創建用戶
  const handleCreateUser = async () => {
    // 驗證角色權限
    if (!isRoleAllowed(createForm.role)) {
      showSnackbar('您沒有權限創建此角色的用戶', 'error');
      return;
    }

    try {
      setLoading(true);
      createForm.role = createForm.role === 'STAFF' ? "" : createForm.role;
      const response = await apiService.createUser(createForm);
      if (response.success) {
        showSnackbar('用戶創建成功', 'success');
        setOpenCreateDialog(false);
        // 設置默認角色為允許列表中的第一個角色
        const allowedRoles = getAllowedRoles();
        setCreateForm({
          userId: '',
          password: '',
          confirmPassword: '',
          nickName: '',
          role: allowedRoles.length > 0 ? allowedRoles[0] : ROLES.STAFF,
          isActive: true
        });
        loadUsers();
      } else {
        showSnackbar(response.message || '創建用戶失敗', 'error');
      }
    } catch (error: any) {
      showSnackbar('創建用戶失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 更新用戶
  const handleUpdateUser = async () => {
    // 驗證角色權限
    const roleToCheck = updateForm.role || ROLES.STAFF;
    if (!isRoleAllowed(roleToCheck)) {
      showSnackbar('您沒有權限將用戶設置為此角色', 'error');
      return;
    }

    try {
      setLoading(true);
      updateForm.role = updateForm.role === 'STAFF' ? "" : updateForm.role;
      const response = await apiService.updateUser(editingUser?.userId || '', updateForm);
      if (response.success) {
        showSnackbar('用戶更新成功', 'success');
        setOpenEditDialog(false);
        setEditingUser(null);
        loadUsers();
      } else {
        showSnackbar(response.message || '更新用戶失敗', 'error');
      }
    } catch (error: any) {
      showSnackbar('更新用戶失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 更改密碼
  const handleChangePassword = async () => {
    try {
      setLoading(true);
      const response = await apiService.changePassword(editingUser?.userId || '', passwordForm);
      if (response.success) {
        showSnackbar('密碼更改成功', 'success');
        setOpenPasswordDialog(false);
        setEditingUser(null);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        showSnackbar(response.message || '更改密碼失敗', 'error');
      }
    } catch (error: any) {
      showSnackbar('更改密碼失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 刪除用戶
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('確定要刪除這個用戶嗎？')) return;

    try {
      setLoading(true);
      const response = await apiService.deleteUser(userId);
      if (response.success) {
        showSnackbar('用戶刪除成功', 'success');
        loadUsers();
      } else {
        showSnackbar(response.message || '刪除用戶失敗', 'error');
      }
    } catch (error: any) {
      showSnackbar('刪除用戶失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 打開編輯對話框
  const openEditDialogHandler = (user: UserInfo) => {
    setEditingUser(user);
    const userRole = user.roleId || ROLES.STAFF;
    const allowedRoles = getAllowedRoles();
    // 如果當前用戶的角色不在允許列表中，使用允許列表中的第一個角色作為默認值
    const defaultRole = isRoleAllowed(userRole) ? userRole : (allowedRoles.length > 0 ? allowedRoles[0] : ROLES.STAFF);
    setUpdateForm({
      userId: user.userId,
      nickName: user.nickName,
      role: defaultRole,
      isActive: user.active === '1'
    });
    setOpenEditDialog(true);
  };

  // 打開密碼更改對話框
  const openPasswordDialogHandler = (user: UserInfo) => {
    setEditingUser(user);
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setOpenPasswordDialog(true);
  };

  // 獲取當前用戶可以創建/編輯的角色列表
  const getAllowedRoles = (): string[] => {
    if (isAdmin) {
      // admin 可以創建/編輯 admin/manager/staff/reader
      return [ROLES.ADMIN, ROLES.MANAGER, ROLES.STAFF, ROLES.READER];
    } else if (roleId === ROLES.MANAGER) {
      // manager 只能創建/編輯 manager/staff/reader
      return [ROLES.MANAGER, ROLES.STAFF, ROLES.READER];
    } else {
      // reader 和 staff 不能創建
      return [];
    }
  };

  // 檢查角色是否在允許的範圍內
  const isRoleAllowed = (role: string): boolean => {
    return getAllowedRoles().includes(role);
  };

  // 檢查是否可以編輯特定用戶（基於用戶的角色）
  const canEditUser = (user: UserInfo): boolean => {
    if (!canCreateUser) {
      return false; // reader 和 staff 不能編輯
    }
    const userRole = user.roleId || ROLES.STAFF;
    return isRoleAllowed(userRole);
  };

  // 獲取角色顯示名稱
  const getRoleDisplayName = (roleId: string | undefined | null) => {
    const normalizedRoleId = roleId || ROLES.STAFF;
    switch (normalizedRoleId) {
      case ROLES.ADMIN: return '管理員';
      case ROLES.MANAGER: return '經理';
      case ROLES.READER: return '讀者';
      case ROLES.STAFF: return '員工';
      default: return '未知';
    }
  };

  // 獲取角色顏色
  const getRoleColor = (roleId: string | undefined | null) => {
    const normalizedRoleId = roleId || ROLES.STAFF;
    switch (normalizedRoleId) {
      case ROLES.ADMIN: return 'error';
      case ROLES.MANAGER: return 'warning';
      case ROLES.READER: return 'info';
      case ROLES.STAFF: return 'default';
      default: return 'default';
    }
  };

  if (!canViewData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          您沒有權限查看用戶管理頁面
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 搜索和過濾控制 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="搜索用戶"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="輸入用戶ID或姓名"
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>角色</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  handleFilterChange();
                }}
                label="角色"
              >
                <MenuItem value="ALL">全部</MenuItem>
                {isAdmin && <MenuItem value={ROLES.ADMIN}>管理員</MenuItem>}
                <MenuItem value={ROLES.MANAGER}>經理</MenuItem>
                <MenuItem value={ROLES.READER}>讀者</MenuItem>
                <MenuItem value={ROLES.STAFF}>員工</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>狀態</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setActiveFilter(e.target.value);
                  handleFilterChange();
                }}
                label="狀態"
              >
                <MenuItem value="ALL">全部</MenuItem>
                <MenuItem value="true">啟用</MenuItem>
                <MenuItem value="false">停用</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={<SearchIcon />}
              >
                搜索
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                刷新
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* 操作按鈕 */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        {canCreateUser && getAllowedRoles().length > 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // 設置默認角色為允許列表中的第一個角色
              const allowedRoles = getAllowedRoles();
              setCreateForm({
                ...createForm,
                role: allowedRoles.length > 0 ? allowedRoles[0] : ROLES.STAFF
              });
              setOpenCreateDialog(true);
            }}
            disabled={loading}
          >
            創建用戶
          </Button>
        )}
      </Box>

      {/* 用戶列表 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>用戶ID</TableCell>
                <TableCell>姓名</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>創建人</TableCell>
                <TableCell>最後上線時間</TableCell>
                <TableCell>創建時間</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{user.userId}</TableCell>
                  <TableCell>{user.nickName}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.active === '1' ? '啟用' : '停用'}
                      color={user.active === '1' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleDisplayName(user.roleId)}
                      color={getRoleColor(user.roleId) as 'error' | 'warning' | 'info' | 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.createdBy}</TableCell>
                  <TableCell>{user.lastLoginTime ? new Date(user.lastLoginTime).toLocaleString('en-GB') : ''}</TableCell>
                  <TableCell>{new Date(user.createdOn).toLocaleString('en-GB')}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {canEditUser(user) && (
                        <Tooltip title="編輯用戶">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialogHandler(user)}
                            disabled={loading}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canEditUser(user) && (
                        <Tooltip title="更改密碼">
                          <IconButton
                            size="small"
                            onClick={() => openPasswordDialogHandler(user)}
                            disabled={loading}
                          >
                            <LockIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 分頁控制 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          顯示 {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} 項，共 {pagination.totalCount} 項
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">每頁顯示:</Typography>
          <Select
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            size="small"
            sx={{ minWidth: 80 }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Stack>
      </Box>

      {/* 創建用戶對話框 */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>創建新用戶</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="用戶ID"
                value={createForm.userId}
                onChange={(e) => setCreateForm({ ...createForm, userId: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓名"
                value={createForm.nickName}
                onChange={(e) => setCreateForm({ ...createForm, nickName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="密碼"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="確認密碼"
                type="password"
                value={createForm.confirmPassword}
                onChange={(e) => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>角色</InputLabel>
                <Select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                >
                  {getAllowedRoles().map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>取消</Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : '創建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編輯用戶對話框 */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>編輯用戶</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="用戶ID"
                value={updateForm.userId}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓名"
                value={updateForm.nickName}
                onChange={(e) => setUpdateForm({ ...updateForm, nickName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>角色</InputLabel>
                <Select
                  value={updateForm.role}
                  onChange={(e) => setUpdateForm({ ...updateForm, role: e.target.value })}
                >
                  {getAllowedRoles().map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>狀態</InputLabel>
                <Select
                  value={updateForm.isActive}
                  onChange={(e) => setUpdateForm({ ...updateForm, isActive: e.target.value === 'true' })}
                >
                  <MenuItem value="true">啟用</MenuItem>
                  <MenuItem value="false">停用</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>取消</Button>
          <Button onClick={handleUpdateUser} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 更改密碼對話框 */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>更改密碼</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="舊密碼"
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="新密碼"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="確認新密碼"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>取消</Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : '更改密碼'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提示訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
